import { Link } from '@tanstack/react-router'
import { ArrowRight, BookText } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useJournalList } from '@/features/ledger/queries'
import { formatShortDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { useOperatorBranchId } from './operator-branch'

const RECENT_COUNT = 5

/**
 * The last few postings at this branch — a glance at whether the ledger is moving at all this
 * morning, not a worklist. `journalListQueryOptions` answers newest first with no ordering
 * parameter to ask otherwise, so the first `RECENT_COUNT` rows of page one are exactly "most
 * recent".
 *
 * Debit and credit are never shown separately here: a posted entry balances by construction — the
 * ledger refuses one that does not — so one figure per entry, its debit total, says the whole
 * amount without implying there is a second number to reconcile it against.
 */
export function JournalActivityTile({ className }: { className?: string }) {
  const branchId = useOperatorBranchId()
  const query = useJournalList({ branchId, pageSize: RECENT_COUNT }, true)

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookText className="size-4 text-muted" aria-hidden />
          Recent journal activity
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        {query.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="Recent journal entries could not be read"
          />
        ) : query.data.items.length === 0 ? (
          <p className="text-sm text-muted">Nothing has posted at this branch yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {query.data.items.map((entry) => (
              <li key={entry.publicId} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-heading">{entry.description}</p>
                  <p className="flex items-center gap-1.5 text-2xs text-muted">
                    <span className="font-mono">{entry.entryNumber}</span>
                    <span aria-hidden>·</span>
                    <span className="numeric tabular-nums">{formatShortDate(entry.businessDate)}</span>
                  </p>
                </div>
                <MoneyDisplay
                  value={entry.totalDebitMinorUnits}
                  currency={entry.currencyCode}
                  className="w-auto shrink-0 text-right"
                />
              </li>
            ))}
          </ul>
        )}

        <Button asChild variant="secondary" size="sm" className="self-start">
          <Link to="/ledger/journals">
            View journal
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
